import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

export function WorkflowAnalytics() {
  const [workflowStats, setWorkflowStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorkflowAnalytics();
  }, []);

  const loadWorkflowAnalytics = async () => {
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile?.org_id) return;

      // Get workflow templates with instance counts
      const { data: templates } = await supabase
        .from("workflow_templates")
        .select(`
          *,
          instances:workflow_instances(count, status, completed_at, started_at)
        `)
        .eq("org_id", profile.org_id);

      if (templates) {
        const stats = templates.map((template: any) => {
          const instances = template.instances || [];
          const total = instances.length;
          const completed = instances.filter((i: any) => i.status === "completed").length;
          const active = instances.filter((i: any) => i.status === "active").length;
          const failed = instances.filter((i: any) => i.status === "cancelled").length;
          const successRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

          // Calculate average completion time for completed workflows
          const completedInstances = instances.filter(
            (i: any) => i.status === "completed" && i.completed_at && i.started_at
          );
          let avgCompletionTime = 0;
          if (completedInstances.length > 0) {
            const totalTime = completedInstances.reduce((sum: number, i: any) => {
              const start = new Date(i.started_at).getTime();
              const end = new Date(i.completed_at).getTime();
              return sum + (end - start);
            }, 0);
            avgCompletionTime = Math.round(totalTime / completedInstances.length / (1000 * 60)); // in minutes
          }

          return {
            id: template.id,
            name: template.name,
            category: template.category,
            isActive: template.is_active,
            total,
            completed,
            active,
            failed,
            successRate,
            avgCompletionTime,
          };
        });

        setWorkflowStats(stats);
      }
    } catch (error: any) {
      console.error("Error loading workflow analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading workflow analytics...</div>;
  }

  if (workflowStats.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">No workflow data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Workflow Performance</h3>

      <div className="grid gap-4">
        {workflowStats.map((stat) => (
          <Card key={stat.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{stat.name}</CardTitle>
                  <CardDescription className="mt-1 space-x-2">
                    <Badge variant="outline">{stat.category}</Badge>
                    <Badge variant={stat.isActive ? "default" : "secondary"}>
                      {stat.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </CardDescription>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{stat.successRate}%</div>
                  <p className="text-xs text-muted-foreground">Success Rate</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span>Completion Progress</span>
                  <span className="text-muted-foreground">
                    {stat.completed} / {stat.total}
                  </span>
                </div>
                <Progress value={stat.successRate} />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <div>
                    <div className="text-xl font-bold">{stat.completed}</div>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <div>
                    <div className="text-xl font-bold">{stat.active}</div>
                    <p className="text-xs text-muted-foreground">Active</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <div>
                    <div className="text-xl font-bold">{stat.failed}</div>
                    <p className="text-xs text-muted-foreground">Failed</p>
                  </div>
                </div>
                <div>
                  <div className="text-xl font-bold">{stat.avgCompletionTime}m</div>
                  <p className="text-xs text-muted-foreground">Avg Time</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
